import React from 'react';
import { BRAND_LOGO_SIZE, BrandLogoSize } from './tokens';

/**
 * Variants for the canonical Rynora brand mark.
 *
 * History (May 2026):
 *   1. Original 24 / 32 / 56 logo with 16 / 18 / 24 wordmark fonts.
 *      The 1.5x–2.3x logo:wordmark ratios made the logo image dwarf
 *      the text in every lockup.
 *   2. Unification pass collapsed everything to 32 / 20 ("regular
 *      everywhere"). Removed the cross-surface jump but the 1.6x
 *      mismatch persisted — testers flagged that the logo was
 *      still visibly taller than the "Rynora" wordmark.
 *   3. Restored per-variant differentiation AND locked logo
 *      height to wordmark font-size (1:1) in each variant, so the
 *      logo's bounding box matches the wordmark em-box and the
 *      whole lockup reads as one piece. Call sites pick the variant
 *      that matches the page's visual weight.
 *
 * Per-variant sizes (logo px / wordmark px / subtitle px):
 *
 * - `compact`       → 24/24/14 — sidebar, dashboard header, dense toolbars
 * - `regular`       → 32/32/14 — login, invite-accept, page headers
 * - `hero`          → 56/56/18 — welcome wizard, splash, full-screen onboarding
 * - `icon-only`     → 24px logo, no text (tight toolbars)
 * - `wordmark-only` → 24px wordmark, no logo (text-only headers, footer "© Rynora")
 *
 * Logo px values live in `BRAND_LOGO_SIZE` in `tokens.ts`; matching
 * wordmark + subtitle sizes live in the `.brand-mark--<variant>`
 * rules in `tokens.css`. Keep them in sync — if you re-tune one,
 * re-tune the other in the same edit to preserve the 1:1 rule.
 */
export type BrandMarkVariant = 'compact' | 'regular' | 'hero' | 'icon-only' | 'wordmark-only';

interface BrandMarkProps {
  variant?: BrandMarkVariant;
  /**
   * Optional subtitle rendered next to the wordmark (e.g. "Help",
   * "Flow Builder", "Admin", "Logs", "Settings"). Hidden for `icon-only`.
   */
  subtitle?: string;
  /**
   * Source URL for the logo asset. Defaults to "/rynora-logo.png" so
   * each consuming app's `public/rynora-logo.png` is picked up. Override
   * if your app serves the logo from a different path.
   */
  logoSrc?: string;
  className?: string;
}

const VARIANT_TO_SIZE: Record<Exclude<BrandMarkVariant, 'wordmark-only'>, BrandLogoSize> = {
  compact: 'compact',
  regular: 'regular',
  hero: 'hero',
  'icon-only': 'compact',
};

/**
 * Aspect ratio (width / height) of the canonical `rynora-logo.png` asset.
 *
 * The PNG is 302x434, i.e. taller than wide (~0.696). If we render it
 * into a square `width={N} height={N}` box (the obvious thing to do
 * when "logo size" is one number), `object-fit: contain` letterboxes
 * the image and the IMG element ends up with ~5px of transparent
 * horizontal padding on each side at the regular 32px variant — which
 * stacks on top of the CSS `gap` and reads as a visibly wide whitespace
 * between the logo and the wordmark. Computing width from height via
 * this ratio makes the IMG bounding box equal to the visible mark,
 * eliminating that phantom gap while preserving width/height attributes
 * (so the browser still reserves the correct slot and avoids CLS while
 * the asset loads). Update this constant if the canonical asset is
 * ever recropped.
 */
const LOGO_ASPECT_RATIO = 302 / 434;

/**
 * Single source of truth for the Rynora logo + wordmark across every
 * Rynora React surface.
 *
 * Replaces the per-page rolled-own brand strips that previously rendered
 * the logo at 24/27/28/56px depending on screen and sometimes dropped
 * the wordmark entirely.
 */
export function BrandMark({ variant = 'compact', subtitle, logoSrc = '/rynora-logo.png', className }: BrandMarkProps) {
  const showLogo = variant !== 'wordmark-only';
  const showWordmark = variant !== 'icon-only';
  const logoSize = variant === 'wordmark-only' ? 0 : BRAND_LOGO_SIZE[VARIANT_TO_SIZE[variant]];
  const logoWidth = Math.round(logoSize * LOGO_ASPECT_RATIO);

  return (
    <span className={`brand-mark brand-mark--${variant}${className ? ` ${className}` : ''}`}>
      {showLogo && (
        <img
          src={logoSrc}
          alt="Rynora"
          width={logoWidth}
          height={logoSize}
          className="brand-mark__logo"
          draggable={false}
        />
      )}
      {showWordmark && (
        <span className="brand-mark__text">
          <span className="brand-mark__wordmark">Rynora</span>
          {subtitle && (
            <>
              <span className="brand-mark__sep" aria-hidden="true">
                ·
              </span>
              <span className="brand-mark__subtitle">{subtitle}</span>
            </>
          )}
        </span>
      )}
    </span>
  );
}
